/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import NumberFormat, {FormatInputValueFunction, NumberFormatProps} from 'react-number-format';

import KeyboardArrowDownIcon from '@edna/icons/keyboardArrowDown.svg';
import {mergeRefs} from '@edna/utils';

import {TComponentRef, formField, newFormField} from '../FormField';
import TextInput, {TRenderTextInputProps, TProps as TTextInputProps, TValue} from '../TextInput';
import * as S from './style';

export type TProps = TComponentRef &
  TTextInputProps & {
    mask?: string | string[];
    numberFormat?: string | FormatInputValueFunction;
    isInteger?: boolean;
    isDecimal?: boolean;
    decimalScale?: number;
    thousandSeparator?: boolean | string;
    allowNegative?: boolean;
  };

type TNumberContentProps = TProps &
  TRenderTextInputProps & {
    type: NumberFormatProps['type'];
  };

const ArrowsInput = React.memo<TAnyObject>(({innerRef, ...props}) => {
  const inputRef = React.useRef<any>();

  return (
    <S.ArrowInputContainer>
      <S.ArrowInput
        {...props}
        ref={innerRef ? mergeRefs(innerRef, inputRef) : inputRef}
        type="number"
      />
      <S.ArrowsContainer>
        <S.ArrowUp
          onClick={() => {
            inputRef.current?.stepUp();
            inputRef.current?.dispatchEvent(new Event('input', {bubbles: true}));
          }}
        >
          <KeyboardArrowDownIcon />
        </S.ArrowUp>
        <S.ArrowDown
          onClick={() => {
            inputRef.current?.stepDown();
            inputRef.current?.dispatchEvent(new Event('input', {bubbles: true}));
          }}
        >
          <KeyboardArrowDownIcon />
        </S.ArrowDown>
      </S.ArrowsContainer>
    </S.ArrowInputContainer>
  );
});

const NumberComponent = React.memo<TNumberContentProps>(
  ({
    mask,
    numberFormat,
    isInteger,
    isDecimal,
    decimalScale,
    thousandSeparator,
    allowNegative = false,
    type,
    onChange,
    innerRef,
    ...inputProps
  }) => {
    const handleChange = React.useCallback(
      (formatValues) => {
        if (formatValues && onChange !== undefined) {
          onChange({
            target: {value: formatValues.formattedValue},
          } as React.ChangeEvent<HTMLInputElement>);
        }
      },
      [onChange],
    );

    return (
      <NumberFormat
        {...inputProps}
        type={type}
        defaultValue=""
        customInput={S.Input}
        onValueChange={handleChange}
        mask={mask}
        format={numberFormat}
        decimalScale={isDecimal ? decimalScale : 0}
        allowLeadingZeros={!isInteger}
        thousandSeparator={thousandSeparator}
        allowNegative={allowNegative}
        getInputRef={innerRef}
      />
    );
  },
);

export const NumberInput = React.memo<TProps>(
  ({mask, numberFormat, isInteger, isDecimal, thousandSeparator, innerRef, ...props}) => (
    <TextInput
      {...props}
      renderInput={({type, ...inputProps}) => (
        <NumberComponent
          {...inputProps}
          innerRef={innerRef}
          type={type as NumberFormatProps['type']}
          mask={mask}
          numberFormat={numberFormat}
          isInteger={isInteger}
          isDecimal={isDecimal}
          thousandSeparator={thousandSeparator}
        />
      )}
    />
  ),
);

const NumberStepInput = React.memo<TProps>(({innerRef, ...props}) => (
  <TextInput {...props} renderInput={(inputProps) => <ArrowsInput {...inputProps} />} />
));

export default NumberInput;

export {NumberStepInput};

export const NumberInputField = formField<TProps, TValue>(NumberInput);

export const NewNumberInputField = newFormField<TProps>(NumberInput);
export const NewNumberStepInputField = newFormField<TProps>(NumberStepInput);
/* eslint-enable react/jsx-props-no-spreading */
